/**
 * Single source of truth for the marketing pages.
 *
 * Copy, plans and contact details live here rather than inside the JSX so the
 * marketing team can change wording, prices and addresses without touching a
 * component.
 *
 * ⚠️ REPLACE THE PLACEHOLDER CONTACT DETAILS below with the real ones before
 * this goes in front of customers.
 */

export const CONTACT = {
  sales: "sales@projam.in",
  support: "projamapps@gmail.com",
  general: "projamapps@gmail.com",
  phone: "+91 40 4651 5454",
  phoneHref: "+91 9014559982",
  hours: "Monday to Friday, 9:00 – 18:00 IST",
  addressLines: ["Projam App", "Hyderabad, India"],
  responseTime: "within one business day",
};

/**
 * Plans shown on the pricing page.
 *
 * The paid tiers mirror the options offered in the registration stepper
 * (components/Stepper/index.jsx). Keep the two in step — a price that differs
 * between the pricing page and the sign-up flow costs a sale.
 */
export const PLANS = [
  {
    id: "Free",
    name: "Free",
    free: true,
    trialDays: 60,
    price: 0,
    users: 500,
    tagline: "The whole product, on your own backlog, for two months.",
    billingNote: "Free for 60 days",
    best: "Evaluating Projam with a real sprint, not a sample project",
    ctaLabel: "Start free trial",
    ctaTo: "/register",
    features: [
      "Full access to every feature for 60 days",
      "Up to 500 users",
      "Unlimited projects and sprints",
      "Sprint board, backlog, epics and estimates",
      "Burn-down and sprint reports",
      "Organisation workspace and role-based access",
      "No credit card required",
      "Email support",
    ],
  },
  {
    id: "Basic",
    name: "Basic",
    price: 50,
    termMonths: 1,
    users: 50,
    tagline: "For a single team finding its rhythm.",
    billingNote: "Billed monthly",
    best: "One team, one project cadence",
    ctaLabel: "Choose Basic",
    ctaTo: "/Organisation/register",
    features: [
      "Up to 200 users",
      "Unlimited projects and sprints",
      "Sprint board with custom workflow columns",
      "Backlog, epics and story point estimates",
      "Burn-down and sprint reports",
      "Comments, mentions and issue history",
      "Email and in-app notifications",
      "Email support",
    ],
  },
  {
    id: "Gold",
    name: "Gold",
    price: 100,
    termMonths: 6,
    users: 100,
    popular: true,
    tagline: "For an organisation standardising on one way of working.",
    billingNote: "Billed every 6 months",
    best: "A whole organisation on one workspace",
    ctaLabel: "Choose Gold",
    ctaTo: "/Organisation/register",
    features: [
      "Up to 500 users",
      "Everything in Basic",
      "Role-based access for managers, leads and scrum masters",
      "Organisation workspace with central administration",
      "Cross-project issue search and filters",
      "Sprint-over-sprint history",
      "Project access control per team",
      "Full audit history on every issue",
      "Priority support with onboarding help",
    ],
  },
];

/** Monthly equivalent, so the term-based prices can be compared honestly. */
export const perMonth = (plan) => Math.round(plan.price / plan.termMonths);

/**
 * Column order follows PLANS. The trial column is ticked wherever the product
 * itself is — that is what "full access" means — while the support rows stay
 * with the paid plans, since those are a service rather than a feature.
 */
export const COMPARISON = [
  {
    group: "Plan limits",
    rows: [
      { label: "Users included", values: ["500", "200", "500"] },
      { label: "Projects", values: ["Unlimited", "Unlimited", "Unlimited"] },
      { label: "Sprints and backlog items", values: ["Unlimited", "Unlimited", "Unlimited"] },
      { label: "Term", values: ["60 days", "1 month", "6 months"] },
    ],
  },
  {
    group: "Plan and track",
    rows: [
      { label: "Sprint board with custom columns", values: [true, true, true] },
      { label: "Backlog grooming and epics", values: [true, true, true] },
      { label: "Story point estimation", values: [true, true, true] },
      { label: "Burn-down and sprint reports", values: [true, true, true] },
      { label: "Sprint-over-sprint history", values: [true, false, true] },
    ],
  },
  {
    group: "Collaborate",
    rows: [
      { label: "Threaded comments and mentions", values: [true, true, true] },
      { label: "Full issue change history", values: [true, true, true] },
      { label: "Email and in-app notifications", values: [true, true, true] },
      { label: "Cross-project search and filters", values: [true, false, true] },
    ],
  },
  {
    group: "Administer",
    rows: [
      { label: "Role-based permissions", values: [true, false, true] },
      { label: "Organisation workspace", values: [true, false, true] },
      { label: "Per-project access control", values: [true, false, true] },
    ],
  },
  {
    group: "Support",
    rows: [
      { label: "Email support", values: [true, true, true] },
      { label: "Priority queue", values: [false, false, true] },
      { label: "Guided onboarding", values: [false, false, true] },
    ],
  },
];

export const PRICING_FAQ = [
  {
    q: "What is included in the 60-day free trial?",
    a: "Everything. The trial is the complete product — board, backlog, sprints, reports, the organisation workspace and role-based access — for a full 60 days, so you can run real sprints rather than poke at a demo.",
  },
  {
    q: "Do I need a credit card to start?",
    a: "No. You create a workspace, invite your team and start planning. We only ask for payment details if you decide to continue on a paid plan.",
  },
  {
    q: "What happens when the 60 days end?",
    a: "Nothing is deleted. Your projects, issues and history stay exactly as they are — you simply choose Basic or Gold to carry on working in them.",
  },
  {
    q: "How does billing work?",
    a: "Each paid plan is licensed for a fixed term — one month or six — and covers everyone in your workspace up to the user limit for that plan. You pay once per term, not per seat per month.",
  },
  {
    q: "What counts as a user?",
    a: "Anyone with an account in your organisation workspace who can open a project. People you have removed no longer count towards the limit.",
  },
  {
    q: "Can we change plan later?",
    a: "Yes. Move from the trial to a paid plan, or from Basic to Gold, at any point — the change applies to your workspace straight away and nothing needs migrating.",
  },
  {
    q: "What happens when we reach the user limit?",
    a: "Existing work is untouched — you simply cannot add new members until you move to a plan with a higher limit. Nothing is deleted or locked.",
  },
  {
    q: "Is our data ours?",
    a: "Yes. Your projects, issues and history belong to your organisation, and we will export them for you on request.",
  },
  {
    q: "Do you offer a plan for a larger organisation?",
    a: "If you need more than 500 users, a longer term or specific security arrangements, talk to us and we will put a proposal together.",
  },
];
